import React, { useState } from 'react';
import { vendorPricesApi, vendorsApi } from '../../utils/api';

const fmt = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

// Fetch date-keyed offers for `stem` across enabled vendors for a single window
// [date, date+1) and let the caller pick one. `date` (ISO) optional → near-term.
export default function VendorOfferPicker({ stem: initialStem = '', date, onPick, onResult }) {
  const [stem, setStem] = useState(initialStem);
  const [offers, setOffers] = useState(null);
  const [busy, setBusy] = useState(false);
  const [usedCurrent, setUsedCurrent] = useState(false);

  const windowFor = () => {
    const fmtIso = (d) => d.toISOString().slice(0, 10);
    if (date) {
      return { start: date, end: fmtIso(new Date(Date.parse(`${date}T00:00:00Z`) + 86400000)) };
    }
    const today = new Date();
    return { start: fmtIso(today), end: fmtIso(new Date(today.getTime() + 7 * 86400000)) };
  };

  const search = async () => {
    setBusy(true);
    try {
      const enabled = ((await vendorsApi.list())?.data || []).filter((v) => v.connection_status === 'connected');
      const fetchFor = async ({ start, end }) => {
        await Promise.all(enabled.map((v) =>
          vendorPricesApi.fetch({ vendor_id: v.id, stems: [stem], date_start: start, date_end: end }).catch(() => null)));
        return enabled.length
          ? (await vendorPricesApi.offers({ vendorIds: enabled.map((v) => v.id), stems: [stem], dateStart: start, dateEnd: end }))?.data || []
          : [];
      };
      const w = windowFor();
      let rows = await fetchFor(w);
      let fellBack = false;
      if (!rows.length && date) {
        const today = new Date();
        const fmtIso = (x) => x.toISOString().slice(0, 10);
        rows = await fetchFor({ start: fmtIso(today), end: fmtIso(new Date(today.getTime() + 86400000)) });
        fellBack = rows.length > 0;
      }
      setUsedCurrent(fellBack);
      setOffers(rows);
      if (typeof onResult === 'function') onResult(rows.length);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input value={stem} onChange={(e) => setStem(e.target.value)} placeholder="stem"
          className="input-field h-ctl-sm min-w-0 flex-1 px-2 sm:text-[13px]" />
        <button type="button" onClick={search} disabled={!stem || busy}
          className="btn-secondary btn-sm shrink-0">
          {busy ? 'Searching…' : 'Search vendors'}
        </button>
      </div>
      {usedCurrent && (
        <div className="text-[11px] text-warn-fg">current price — the requested date is beyond the vendor horizon</div>
      )}
      {offers && (
        <div className="rounded-md border border-gray-200 divide-y divide-gray-200 max-h-64 overflow-auto bg-white">
          {offers.length === 0 && <div className="text-xs text-gray-500 p-2">No offers.</div>}
          {offers.map((o, i) => (
            <button type="button" key={i} onClick={() => onPick?.(o)}
              className="w-full flex justify-between items-center gap-2 text-left text-xs text-gray-900 px-2 py-1.5 hover:bg-floral-50">
              <span>{o.product_name || o.variety || stem} · {o.vendor_name || o.vendor_id}</span>
              <span className="tabular-nums shrink-0">${o.price_per_stem}{o.attributes?.arrival_date ? ` · ${fmt(o.attributes.arrival_date)}` : ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
